import { useEffect, useRef, useState } from "react";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { AddressAutocompleteV2 } from "components/AddressAutocompleteV2";
import { ManualRoutePointInput } from "components/ManualRoutePointInput";

import { TimeInput } from "../../../../../../TimeInput";
import { getTimeInputLabel } from "../../../utils";

import type { SearchBoxFeatureSuggestion } from "@mapbox/search-js-core";

interface Props {
  index: number;
  routePoint: EditableRoutePointState;
  onRoutePointTimeChange: (key: "start_time", value: string) => void;
  onScheduleTypeUpdate: (value: RoutePointScheduleType) => void;
  onAddressSuggestionSelected: (feature: SearchBoxFeatureSuggestion) => void;
  onAddressCleared: () => void;
  handleRoutePointLocationValueChange: <T>(
    key: keyof RoutePointLocation,
    value: T | null,
    index: number,
  ) => void;
  updateInputModeForRoutePoint: (
    index: number,
    mode: "autocomplete" | "manual",
  ) => void;
  deleteMidRoutePointButton: React.ReactNode;
}

export function EditableRoutePoint(props: Props) {
  const {
    index,
    routePoint,
    onRoutePointTimeChange,
    onAddressSuggestionSelected,
    onAddressCleared,
    handleRoutePointLocationValueChange,
    updateInputModeForRoutePoint,
    onScheduleTypeUpdate,
    deleteMidRoutePointButton,
  } = props;

  const [shouldOpenPicker, setShouldOpenPicker] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (shouldOpenPicker && dateInputRef.current) {
      (
        dateInputRef.current as HTMLInputElement & { showPicker?: () => void }
      ).showPicker?.();
      setShouldOpenPicker(false);
    }
  }, [shouldOpenPicker]);

  const isDropOff = routePoint.stop_operation === "DROPOFF";
  const isAsap = routePoint.schedule_type === "ASAP";

  const handleAsapFieldClick = () => {
    onScheduleTypeUpdate("DATETIME");
    setShouldOpenPicker(true);
  };

  const getAddressContent = () => {
    let addressInput = <p>Error</p>;
    if (routePoint.inputMode === "autocomplete") {
      addressInput = (
        <Box sx={{ width: "100%" }}>
          <AddressAutocompleteV2
            routePoint={routePoint}
            onSuggestionSelected={onAddressSuggestionSelected}
            onClear={onAddressCleared}
            updateInputModeForRoutePoint={(mode: "autocomplete" | "manual") =>
              updateInputModeForRoutePoint(index, mode)
            }
          />
        </Box>
      );
    } else if (routePoint.inputMode === "manual") {
      const onLocationValueChange = <T,>(
        key: keyof RoutePointLocation,
        value: T | null,
      ) => handleRoutePointLocationValueChange(key, value, index);
      const onInputModeChange = (mode: "autocomplete" | "manual") =>
        updateInputModeForRoutePoint(index, mode);
      addressInput = (
        <ManualRoutePointInput
          routePointLocation={routePoint.point_location as RoutePointLocation}
          handleRoutePointLocationValueChange={onLocationValueChange}
          updateInputModeForRoutePoint={onInputModeChange}
        />
      );
    }

    return addressInput;
  };

  return (
    <Stack direction="column" flexWrap="wrap" spacing={2} width="100%">
      <Box display="flex" alignItems="center">
        <Typography sx={{ textTransform: "capitalize" }} fontSize="1.1rem">
          {routePoint.stop_type?.toLowerCase()}
        </Typography>
      </Box>
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.5}
        sx={{ ml: "1rem" }}
      >
        <Box sx={{ flex: 1 }}>{getAddressContent()}</Box>
        <Stack direction="column" alignItems="flex-end" spacing={0.5}>
          {!isDropOff && (
            <TimeInput
              timeFormat="date"
              label={getTimeInputLabel(routePoint)}
              onChange={(value) => onRoutePointTimeChange("start_time", value)}
              initialValue={routePoint.start_time}
            />
          )}
          {isDropOff && isAsap && (
            <Tooltip title="Click to set a specific delivery date">
              <TextField
                label="Delivery Date"
                value="Flexible"
                onClick={handleAsapFieldClick}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarTodayIcon
                        fontSize="small"
                        sx={{ color: "action.active" }}
                      />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 250,
                  cursor: "pointer",
                  "& input": { cursor: "pointer" },
                }}
              />
            </Tooltip>
          )}
          {isDropOff && !isAsap && (
            <TextField
              label="Delivery Date"
              type="date"
              value={routePoint.start_time ?? ""}
              inputRef={dateInputRef}
              sx={{ width: 250 }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) =>
                onRoutePointTimeChange("start_time", e.target.value)
              }
            />
          )}
          {isDropOff && !isAsap && (
            <Tooltip title="Reset to Flexible">
              <Button
                variant="text"
                size="small"
                onClick={() => onScheduleTypeUpdate("ASAP")}
                sx={{ textTransform: "none", p: 0 }}
                aria-label="Reset to Flexible"
              >
                Reset to Flexible
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {routePoint.stop_type === "MID" && deleteMidRoutePointButton}
    </Stack>
  );
}
